import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { RecordQueueView, SendResult } from '@/hooks/useRecordQueue';
import { notifySuccess } from '@/lib/haptics';
import { formatRecordDuration } from '@/lib/recordQueue';
import { cn } from '@/lib/utils';

/**
 * «Сдать» в «Домашках» (промпт 22; «да» владельца 18.10.2026): у домашки, к разделу которой в очереди лежит запись
 * голоса, — метка «Не отправлена» и кнопка «Сдать»; пока запись уходит — «Отправлена» и полоска. Дальше — обычные
 * статусы домашки: после отправки сервер сам пересобирает сдачу раздела, и строка становится «На проверке».
 * Если сдача у раздела уже есть (запись — вторая попытка) — вставка «Новая запись · Не отправлена», пояснение
 * «заменит ответ…» и «Сдать новую запись» (вариант А, 18.10).
 *
 * ⚠️ ОКНО «СДАТЬ» СО СВОЕЙ ЗАПИСЬЮ ЗДЕСЬ НЕ ТРОНУТО (решение владельца 18.10) — оно по-прежнему шлёт мимо очереди.
 * «Сдать» здесь — только запись, которая уже лежит на устройстве после урока.
 */
export function QueuedRecordLine({
  row,
  onSend,
  replaces,
  className,
}: {
  row: RecordQueueView;
  onSend: () => Promise<SendResult>;
  /** У раздела уже есть сдача: 'pending' — она на проверке, 'sent' — с другим статусом. Тогда — вставка «Новая запись». */
  replaces?: 'pending' | 'sent';
  className?: string;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [offline, setOffline] = useState(false);
  const dur = formatRecordDuration(row.seconds);

  const send = async () => {
    setBusy(true);
    try {
      const res = await onSend();
      setOffline(res === 'offline');
      if (res === 'sent') notifySuccess();
    } finally {
      setBusy(false);
    }
  };

  const sending = row.sending || busy;
  const offlineNote = offline && !sending && (
    <p className="text-[12px] text-on-surface-variant" role="status">
      {t('recordQueue.hwOffline')}
    </p>
  );

  // У раздела уже есть сдача — вариант А владельца 18.10: новая запись своей вставкой под строкой сдачи, с
  // пояснением, что она заменит отправленный ответ. В одной строке «На проверке» + «Сдать» читалось как
  // «сдай заново» (кадры 05–06).
  if (replaces) {
    return (
      <div
        className={cn('space-y-2 rounded-[14px] bg-surface-container-lowest px-3 py-2.5', className)}
        data-hw-queued-record={row.blockId}
        data-hw-queued-replaces={replaces}
      >
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined flex-none text-[18px] text-primary">mic</span>
          <span className="min-w-0 flex-1 text-[12px] text-on-surface">
            {dur ? t('recordQueue.hwNewRecord', { dur }) : t('recordQueue.hwNewRecordNoDur')}
          </span>
          {sending ? (
            <span className="flex-none rounded-full bg-info-container px-2 py-1 text-[10px] font-bold uppercase text-info">
              {t('recordQueue.hwSent')}
            </span>
          ) : (
            <span className="flex-none rounded-full bg-primary-container px-2 py-1 text-[10px] font-bold uppercase text-on-primary-container">
              {t('recordQueue.hwNotSent')}
            </span>
          )}
        </div>
        {!sending && (
          <>
            <p className="text-[12px] leading-snug text-on-surface-variant">
              {t(replaces === 'pending' ? 'recordQueue.hwReplacesPending' : 'recordQueue.hwReplacesSent')}
            </p>
            <button
              type="button"
              onClick={() => void send()}
              className="rounded-full bg-primary px-4 py-[7px] text-[11px] font-bold text-on-primary transition hover:bg-primary-hover"
            >
              {t('recordQueue.hwSubmitNew')}
            </button>
          </>
        )}
        {sending && (
          <div className="h-1 overflow-hidden rounded-full bg-surface-container-low">
            <div className="h-full bg-info transition-[width]" style={{ width: `${row.percent ?? 30}%` }} />
          </div>
        )}
        {offlineNote}
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)} data-hw-queued-record={row.blockId}>
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 text-[12px] text-on-surface-variant">
          {dur ? t('recordQueue.hwRecordMeta', { dur }) : t('recordQueue.hwRecordMetaNoDur')}
        </span>
        {sending ? (
          <span className="flex-none rounded-full bg-info-container px-2 py-1 text-[10px] font-bold uppercase text-info">
            {t('recordQueue.hwSent')}
          </span>
        ) : (
          <>
            <span className="flex-none rounded-full bg-primary-container px-2 py-1 text-[10px] font-bold uppercase text-on-primary-container">
              {t('recordQueue.hwNotSent')}
            </span>
            <button
              type="button"
              onClick={() => void send()}
              className="flex-none rounded-full bg-primary px-4 py-[7px] text-[11px] font-bold text-on-primary transition hover:bg-primary-hover"
            >
              {t('recordQueue.hwSubmit')}
            </button>
          </>
        )}
      </div>
      {sending && (
        <div className="h-1 overflow-hidden rounded-full bg-surface-container-lowest">
          <div className="h-full bg-info transition-[width]" style={{ width: `${row.percent ?? 30}%` }} />
        </div>
      )}
      {offlineNote}
    </div>
  );
}

/** Запись к разделу, у которого сдачи ещё нет (первая попытка ушла в очередь без связи): своя строка сверху. */
export function QueuedOnlyRow({ row, onSend }: { row: RecordQueueView; onSend: () => Promise<SendResult> }) {
  const { t } = useTranslation();
  return (
    <div data-hw-queued-only={row.blockId} className="border-b border-outline py-3 last:border-b-0">
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-primary-container text-primary">
          <span className="material-symbols-outlined text-[18px]">mic</span>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-semibold text-on-surface">
            {row.lessonTitle ?? t('recordQueue.untitled')}
          </span>
          {row.sectionTitle && <span className="block text-[12px] text-on-surface-variant">{row.sectionTitle}</span>}
        </span>
      </div>
      <QueuedRecordLine row={row} onSend={onSend} className="mt-2 pl-12" />
    </div>
  );
}
